import { useMemo } from "react";
import { Link, useSearchParams } from "react-router";

import { useWideLayout } from "../../layout/shell-layout";
import { AsyncState } from "../../ui/async-state";
import { Button } from "../../ui/button";
import { EmptyState } from "../../ui/empty-state";
import { Icon } from "../../ui/icon";
import { Notice } from "../../ui/notice";
import { PageHeader } from "../../ui/page-header";
import { useCatalogCapabilities } from "../access";
import { describeCatalogError } from "../errors";
import {
  activeFilterCount,
  filterValuesToSearchParams,
  queryToFilterValues,
  searchParamsToQuery,
} from "../filters";
import { useCatalogList } from "../queries";
import { useCatalogRuntime } from "../runtime";
import { CatalogFilters } from "./CatalogFilters";
import { CatalogTable } from "./CatalogTable";

/** The catalog list: filters in the address, a keyset-paged table, and "Load more". */
export function CatalogListPage() {
  useWideLayout();
  const [params, setParams] = useSearchParams();
  const parsed = useMemo(() => searchParamsToQuery(params), [params]);
  const filterValues = useMemo(() => queryToFilterValues(parsed.query), [parsed.query]);
  const { capabilities, isLoading: rolesLoading } = useCatalogCapabilities();
  const { now } = useCatalogRuntime();
  const list = useCatalogList(parsed.query, capabilities.read);

  const items = list.data?.pages.flatMap((page) => page.items) ?? [];
  const filterCount = activeFilterCount(parsed.query);

  if (rolesLoading) {
    return (
      <AsyncState kind="loading" title="Checking your access" message="Reading your staff role." />
    );
  }
  if (!capabilities.read) {
    return (
      <AsyncState
        kind="error"
        title="The catalog is not available for your role"
        message="Your staff role does not include catalog access."
      />
    );
  }

  return (
    <div className="grid min-w-0 gap-6">
      <PageHeader
        title="Catalog"
        description={`Dining, villas, events, experiences and services.${capabilities.edit ? "" : " You have read-only access."}`}
        actions={
          <>
            <Button asChild variant="secondary">
              <Link to="/catalog/search">Catalog search</Link>
            </Button>
            <Button asChild variant="secondary">
              <Link to="/catalog/places/review">Places to review</Link>
            </Button>
            {capabilities.edit ? (
              <Button asChild>
                <Link to="/catalog/new">
                  <Icon name="plus-sign" size="md" />
                  New item
                </Link>
              </Button>
            ) : null}
          </>
        }
      />

      {parsed.ignored.length > 0 ? (
        <Notice tone="warning" role="status">
          Some filters in the address were not valid and were ignored: {parsed.ignored.join(", ")}.
        </Notice>
      ) : null}

      <CatalogFilters
        key={params.toString()}
        initial={filterValues}
        onApply={(values) => setParams(filterValuesToSearchParams(values))}
      />

      <div aria-busy={list.isFetching} className="grid min-w-0 gap-4">
        {list.isPending ? (
          <AsyncState
            kind="loading"
            title="Loading the catalog"
            message="Reading the first page."
          />
        ) : list.isError ? (
          <AsyncState
            kind="error"
            title="The catalog could not be loaded"
            message={describeCatalogError(list.error).message}
            onRetry={() => void list.refetch()}
          />
        ) : items.length === 0 ? (
          <EmptyState
            className="rounded-lg border border-hairline bg-card"
            title="No items found"
            description={
              filterCount > 0
                ? "No item matches these filters. Clear the filters to see everything."
                : "There are no catalog items yet."
            }
          />
        ) : (
          <>
            <CatalogTable
              items={items}
              now={now()}
              caption={`Catalog items${filterCount > 0 ? ` (${filterCount} ${filterCount === 1 ? "filter" : "filters"} applied)` : ""}`}
            />
            <div className="flex flex-wrap items-center gap-4">
              <p role="status" className="m-0 text-sm leading-5 text-muted-foreground">
                Showing {items.length} {items.length === 1 ? "item" : "items"}
                {list.hasNextPage ? ". More are available." : ". That is all of them."}
              </p>
              {list.hasNextPage ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => void list.fetchNextPage()}
                  disabled={list.isFetchingNextPage}
                >
                  {list.isFetchingNextPage ? "Loading…" : "Load more"}
                </Button>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
